// Painel Operacional da Contabilidade (/Contabilidade/Operacional).
// Versão objetiva do boletim contábil: cada card diz o número, o que ele significa
// e o que fazer. Só leitura; os números vêm das mesmas consultas do boletim.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';

const COR = {
  navy: '#1d2530', ciano: '#04c2d7', ok: '#1a7f4b', okBg: '#e9f6ef',
  warn: '#a5650a', warnBg: '#fdf3e1', bad: '#c1153d', badBg: '#fdecef',
  soft: '#5b6678', line: '#e3e7ee', surf: '#f6f8fa', azul: '#2b5d9b', azulBg: '#e8f0fb',
};

const tom = (n, alerta, critico) =>
  n >= critico ? [COR.bad, COR.badBg] : n >= alerta ? [COR.warn, COR.warnBg] : [COR.ok, COR.okBg];

const Chip = ({ children, cor = COR.soft, fundo = COR.surf }) => (
  <span className="badge rounded-pill me-1 mb-1"
    style={{ background: fundo, color: cor, fontWeight: 700, fontSize: '0.72rem' }}>{children}</span>
);

const Titulo = ({ children, sub }) => (
  <div className="mt-4 mb-2">
    <h5 className="mb-1" style={{ fontWeight: 800, color: COR.navy }}>{children}</h5>
    <div style={{ width: 32, height: 3, background: COR.ciano, borderRadius: 2 }} />
    {sub && <div className="small mt-2" style={{ color: COR.soft }}>{sub}</div>}
  </div>
);

function ListaEmpresas({ itens }) {
  if (!itens || !itens.length) return null;
  return (
    <div className="mt-2">
      {itens.map((x) => (
        <div key={`${x.dominio}-${x.empresa}`} className="d-flex justify-content-between py-1"
          style={{ borderTop: `1px solid ${COR.line}`, fontSize: '0.8rem' }}>
          <div style={{ minWidth: 0 }}>
            <div className="text-truncate fw-semibold" title={x.empresa}>{x.empresa}</div>
            <div style={{ color: COR.soft, fontSize: '0.72rem' }}>{x.operador}</div>
          </div>
          <div className="fw-bold ms-2 text-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>{x.valor}</div>
        </div>
      ))}
    </div>
  );
}

function CardSaude({ rotulo, numero, detalhe, explica, acao, cor, fundo, children }) {
  return (
    <div className="col-12 col-md-6 d-flex">
      <div className="bg-white rounded-3 p-3 w-100"
        style={{ border: `1px solid ${COR.line}`, borderTop: `4px solid ${cor}` }}>
        <div className="text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '.8px', color: COR.soft }}>{rotulo}</div>
        <div className="mt-1">
          <span style={{ fontSize: '1.9rem', fontWeight: 800, color: cor, fontVariantNumeric: 'tabular-nums' }}>{numero}</span>
          <span className="ms-2 fw-semibold" style={{ color: COR.soft, fontSize: '0.85rem' }}>{detalhe}</span>
        </div>
        <div className="small mt-1">{explica}</div>
        {children}
        <div className="mt-2 rounded-2 px-2 py-2 fw-semibold" style={{ background: fundo, color: cor, fontSize: '0.78rem' }}>{acao}</div>
      </div>
    </div>
  );
}

function Mini({ n, rotulo, alerta, critico }) {
  const cor = !n ? COR.soft : n < alerta ? COR.navy : tom(n, alerta, critico)[0];
  return (
    <div className="text-center px-2 py-1 flex-fill" style={{ borderLeft: `1px solid ${COR.line}`, minWidth: 90 }}>
      <div style={{ fontSize: '1.25rem', fontWeight: 800, color: cor, fontVariantNumeric: 'tabular-nums' }}>{n}</div>
      <div style={{ fontSize: '0.7rem', color: COR.soft, lineHeight: 1.25 }}>{rotulo}</div>
    </div>
  );
}

function CardOcorrencia({ x, borda, rotuloTexto, passoCor, passoTexto }) {
  return (
    <div className="bg-white rounded-3 p-3 mb-2" style={{ border: `1px solid ${COR.line}`, borderLeft: `4px solid ${borda}` }}>
      <div className="fw-bold">{x.empresa}</div>
      <div className="mt-1">
        <Chip cor={COR.navy}>{x.operador}</Chip>
        <Chip cor={borda} fundo={borda === COR.bad ? COR.badBg : COR.warnBg}>{x.tipo}</Chip>
        {x.externo
          ? <Chip cor={COR.azul} fundo={COR.azulBg}>Depende do cliente</Chip>
          : <Chip cor={COR.bad} fundo={COR.badBg}>Depende de nós</Chip>}
        {x.status && <Chip>{x.status}</Chip>}
      </div>
      <div className="small mt-1"><strong>{rotuloTexto}:</strong> {x.texto || 'Sem registro.'}</div>
      <div className="small fw-semibold mt-1" style={{ color: passoCor }}>Próximo passo: {passoTexto}</div>
    </div>
  );
}

export default function PainelOperacionalContabil() {
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const r = await axios.get(`${process.env.REACT_APP_API_BASE}/api/contabil/painel-operacional/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` }, timeout: 120000,
      });
      if (r.data.sucesso) setDados(r.data);
      else setErro(r.data.erro || 'Não foi possível montar o painel agora.');
    } catch (e) {
      setErro(e.response?.data?.erro || 'Não foi possível montar o painel agora. Tente atualizar em instantes.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);


  const s = dados?.saude;
  const hist = dados?.historico || [];
  const varOntem = hist.length >= 2 ? hist[hist.length - 1].atraso - hist[hist.length - 2].atraso : null;
  const varTxt = varOntem === null ? 'sem comparação' : varOntem === 0 ? 'igual a ontem'
    : `${varOntem > 0 ? '▲ subiu' : '▼ caiu'} ${Math.abs(varOntem)} desde ontem`;
  const difPeriodo = hist.length >= 2 ? hist[hist.length - 1].atraso - hist[0].atraso : 0;
  const pctDia = s && s.total ? Math.round((s.em_dia / s.total) * 1000) / 10 : 0;
  const faixa3090 = (dados?.listas.atraso_30 || []).filter((x) => parseInt(x.valor, 10) <= 90);

  return (
    <div className="container-xl mt-4 mb-5">
      <div className="bg-white rounded-3 shadow-sm p-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
        <div className="d-flex align-items-center">
          <i className="fas fa-clipboard-check header-icon me-3"></i>
          <div>
            <h2 className="titulo-pagina">Painel Operacional</h2>
            <div className="small mt-3" style={{ color: COR.soft }}>
              O que precisa de ação hoje na Contabilidade
              {dados && <> · movimentação {dados.periodo.rotulo} · atualizado {dados.gerado_em}</>}
            </div>
          </div>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => navigate('/Contabilidade')}>
            <i className="fas fa-arrow-left me-1"></i>Contabilidade
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={carregar} disabled={carregando}>
            <i className={`fas fa-sync-alt me-1 ${carregando ? 'fa-spin' : ''}`}></i>Atualizar
          </button>
        </div>
      </div>

      {carregando && !dados && (
        <div className="text-center py-5" style={{ color: COR.soft }}>
          <div className="spinner-border mb-2" role="status" />
          <div>Montando o painel com os números de agora…</div>
        </div>
      )}
      {erro && <div className="alert alert-danger mt-3">{erro}</div>}

      {dados && (
        <>
          <Titulo sub="Como está a carteira inteira hoje. Cada card diz o que o número significa e o que fazer.">
            Saúde da carteira
          </Titulo>
          <div className="row g-3">
            <CardSaude rotulo="Empresas em dia" numero={s.em_dia}
              detalhe={`de ${s.total} · ${String(pctDia).replace('.', ',')}%`}
              explica="Empresas com o fechamento dentro do prazo do seu tipo de entrega."
              acao={pctDia >= 90 ? 'Carteira saudável. Manter o ritmo.' : 'Meta: manter acima de 90%.'}
              cor={pctDia >= 90 ? COR.ok : COR.warn} fundo={pctDia >= 90 ? COR.okBg : COR.warnBg} />
            {(() => {
              const [c, f] = (varOntem || 0) > 0 ? [COR.bad, COR.badBg] : tom(s.atraso_30, 15, 30);
              return (
                <CardSaude rotulo="Atraso acima de 30 dias" numero={s.atraso_30} detalhe={varTxt}
                  explica="Empresas com fechamento atrasado há mais de um mês."
                  acao="Cada uma precisa de cobrança registrada ao cliente ou plano de entrega." cor={c} fundo={f}>
                  <ListaEmpresas itens={faixa3090.length ? faixa3090 : dados.listas.atraso_30} />
                </CardSaude>
              );
            })()}
            {(() => {
              const [c, f] = tom(s.atraso_90, 5, 12);
              return (
                <CardSaude rotulo="Atraso acima de 90 dias" numero={s.atraso_90} detalhe="casos crônicos"
                  explica="Empresas paradas há mais de três meses, quase sempre por falta de documento."
                  acao="Levar ao gestor: decidir entre escalar a cobrança ou outboarding." cor={c} fundo={f}>
                  <ListaEmpresas itens={dados.listas.atraso_90} />
                </CardSaude>
              );
            })()}
            {(() => {
              const [c, f] = tom(s.relatorio_pendente, 5, 15);
              return (
                <CardSaude rotulo="Relatório de fechamento pendente" numero={s.relatorio_pendente} detalhe="empresas"
                  explica={`Fechamento entregue há mais de ${s.prazo_relatorio_dias} dias e o e-mail ao cliente ainda não saiu.`}
                  acao="Enviar o relatório pela tela Relatório Contábil." cor={c} fundo={f}>
                  <ListaEmpresas itens={dados.listas.relatorio_pendente} />
                </CardSaude>
              );
            })()}
          </div>

          {hist.length >= 2 && (
            <div className="bg-white rounded-3 p-3 mt-3" style={{ border: `1px solid ${COR.line}` }}>
              <div className="text-uppercase fw-bold" style={{ fontSize: '0.7rem', letterSpacing: '.8px', color: COR.soft }}>
                Atrasos acima de 30 dias · subiu ou caiu?
              </div>
              <div className="fw-bold mt-1" style={{ color: difPeriodo > 0 ? COR.bad : difPeriodo < 0 ? COR.ok : COR.soft }}>
                {difPeriodo === 0 ? 'Estável no período'
                  : `${difPeriodo > 0 ? 'Subiu' : 'Caiu'} ${Math.abs(difPeriodo)} em ${hist.length} boletins (de ${hist[0].atraso} para ${hist[hist.length - 1].atraso})`}
              </div>
              <div className="small mb-2">
                Cada ponto é o total de empresas com mais de 30 dias de atraso no dia do boletim. Linha descendo é a carteira melhorando.
              </div>
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <LineChart data={hist.map((h) => ({ ...h, dia: h.data.slice(8, 10) + '/' + h.data.slice(5, 7) }))}
                    margin={{ top: 22, right: 16, left: -16, bottom: 0 }}>
                    <CartesianGrid stroke={COR.line} vertical={false} />
                    <XAxis dataKey="dia" tick={{ fontSize: 11, fill: COR.soft }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: COR.soft }} />
                    <Tooltip formatter={(v) => [`${v} empresas`, 'Atraso +30 dias']} />
                    <Line type="monotone" dataKey="atraso" stroke={COR.navy} strokeWidth={2.5} dot={{ r: 4, fill: COR.ciano }}>
                      <LabelList dataKey="atraso" position="top" style={{ fontSize: 11, fontWeight: 700, fill: COR.navy }} />
                    </Line>
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <Titulo sub="Quem precisa de apoio primeiro. Ordenado por atrasos acima de 30 dias.">Carteira por operador</Titulo>
          {dados.operadores.map((o) => {
            const selo = (o.atraso_30 >= 5 || o.em_atraso >= 8) ? ['Precisa de apoio', COR.bad, COR.badBg]
              : (o.atraso_30 || o.em_atraso || o.parciais_abertas || o.relatorios) ? ['Atenção', COR.warn, COR.warnBg]
                : ['Em dia', COR.ok, COR.okBg];
            return (
              <div key={o.operador} className="bg-white rounded-3 mb-2 d-flex flex-wrap align-items-center"
                style={{ border: `1px solid ${COR.line}` }}>
                <div className="p-3" style={{ minWidth: 200, flex: '1 1 200px' }}>
                  <div className="fw-bold">{o.operador}</div>
                  <div className="small" style={{ color: COR.soft }}>{o.clientes} clientes na carteira</div>
                  <Chip cor={selo[1]} fundo={selo[2]}>{selo[0]}</Chip>
                </div>
                <div className="d-flex flex-wrap" style={{ flex: '3 1 380px' }}>
                  <Mini n={o.em_atraso} rotulo="em atraso (qualquer prazo)" alerta={4} critico={8} />
                  <Mini n={o.atraso_30} rotulo="atraso +30 dias" alerta={2} critico={5} />
                  <Mini n={o.parciais_abertas} rotulo="entregas parciais em aberto" alerta={3} critico={8} />
                  <Mini n={o.relatorios} rotulo="relatórios a enviar" alerta={2} critico={4} />
                </div>
              </div>
            );
          })}

          <Titulo sub={`Entregas parciais lançadas ${dados.periodo.rotulo}, com o que falta e o próximo passo.`}>
            Pendências registradas ({dados.pendencias.length})
          </Titulo>
          {dados.pendencias.length ? dados.pendencias.map((x) => (
            <CardOcorrencia key={`p-${x.empresa}-${x.tipo}`} x={x} borda={COR.warn} rotuloTexto="O que falta"
              passoCor={COR.warn}
              passoTexto={x.externo ? 'Cobrar o cliente e registrar a cobrança.' : 'Concluir internamente e marcar como resolvida.'} />
          )) : <div className="rounded-3 p-3 fw-semibold" style={{ background: COR.okBg, color: COR.ok }}>Nenhuma pendência registrada {dados.periodo.rotulo}.</div>}
          <button type="button" className="btn btn-link p-0 fw-bold small" style={{ color: COR.navy }}
            onClick={() => navigate('/Pendenciacontabil')}>Ver todas as pendências →</button>

          <Titulo sub={`Entregas lançadas com atraso ${dados.periodo.rotulo}, com o motivo e o próximo passo.${dados.atrasos_repetidos ? ` ${dados.atrasos_repetidos} já aparece(m) em Pendências e não se repete(m) aqui.` : ''}`}>
            Atrasos registrados ({dados.atrasos.length})
          </Titulo>
          {dados.atrasos.length ? dados.atrasos.map((x) => (
            <CardOcorrencia key={`a-${x.empresa}-${x.tipo}`} x={x} borda={COR.bad} rotuloTexto="Motivo do atraso"
              passoCor={COR.bad}
              passoTexto={x.externo ? 'Cobrar o cliente e registrar a cobrança.' : 'Entregar o quanto antes e registrar o motivo para não repetir.'} />
          )) : <div className="rounded-3 p-3 fw-semibold" style={{ background: COR.okBg, color: COR.ok }}>Nenhum atraso registrado {dados.periodo.rotulo}.</div>}
          <button type="button" className="btn btn-link p-0 fw-bold small" style={{ color: COR.navy }}
            onClick={() => navigate('/Atrasoatual')}>Ver os atrasos →</button>
        </>
      )}
    </div>
  );
}
